import { useEffect, useRef } from 'react'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import 'videojs-contrib-eme'
import {
  addCuePointsInPlayer,
  addSubtitlesInPlayer,
  formatCuePoints,
} from '../../Helpers/VideoPlayer'

interface VideoJSInterface {
  source: {
    src: string
    type?: string
    keySystems?: any
  }[]
  subtitles?: any
  cuepoints?: any
  playBackRates?: (0.5 | 0.75 | 1 | 1.25 | 1.5 | 1.75 | 2 | 4)[]
  skipButtons?: 5 | 10
  onReady?: (player: any) => void
}

export const VideoJS = ({
  source,
  subtitles,
  cuepoints,
  playBackRates,
  skipButtons,
  onReady,
}: VideoJSInterface) => {
  const videoRef: any = useRef(null)
  const playerRef: any = useRef(null)

  const videoOptions = {
    autoplay: false,
    controls: true,
    responsive: true,
    fluid: true,
    ...(playBackRates?.length && { playbackRates: playBackRates }),
    controlBar: {
      ...(skipButtons && {
        skipButtons: {
          forward: skipButtons,
          backward: skipButtons,
        },
      }),
    },
  }

  let cuepointsList: any[] = []
  if (cuepoints?.length) {
    cuepointsList = formatCuePoints(cuepoints)
  }

  // Function to handle time updates
  let lastTime = 0
  const handleTimeUpdate = () => {
    if (!cuepointsList.length) {
      return
    }
    // Get the current playback time
    const currentTime = playerRef.current.currentTime()

    // Check if it's time to execute your callback function (e.g., every second)
    if (Math.floor(currentTime) !== Math.floor(lastTime)) {
      addCuePointsInPlayer(Math.floor(currentTime), playerRef.current, cuepointsList)
      // Update the last time
      lastTime = currentTime
    }
  }

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement('video-js')
      videoElement.classList.add('vjs-big-play-centered')
      videoRef.current.appendChild(videoElement)
      const player = (playerRef.current = videojs(videoElement, videoOptions, () => {
        onReady && onReady(player)
      }))
    }
    // You could update an existing player in the `else` block here
    const player = playerRef.current
    if (source?.[0]?.keySystems && typeof player.eme === 'function') {
      player.eme()
    }

    player.autoplay(videoOptions.autoplay)
    player.src(source)

    player.off('timeupdate', handleTimeUpdate)
    player.on('timeupdate', handleTimeUpdate)

    if (subtitles) {
      addSubtitlesInPlayer(subtitles, player)
    }
  }, [source, subtitles])

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose()
        playerRef.current = null
      }
    }
  }, [playerRef])

  return (
    <div data-vjs-player style={{ zIndex: 9, position: 'relative' }}>
      <div ref={videoRef} />
    </div>
  )
}

export default VideoJS
